import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Sidebar";
import { api } from "../../../utils/apiClient";
import { API_ENDPOINTS } from "../../../config/api";
import {
  BarChart3,
  PieChart,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  FileText,
  CheckCircle,
  Clock,
  AlertTriangle,
  Building,
  DollarSign,
  Printer,
} from "lucide-react";

const ReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [reportData, setReportData] = useState({
    totalPlots: 0,
    totalPaid: 0,
    totalPurchasePrice: 0,
    projectStats: [],
    statusDistribution: [],
    recentActivity: [],
  });

  const fetchReportData = async () => {
    try {
      setError(null);
      const { data } = await api.get(API_ENDPOINTS.REPORTS.SUMMARY);
      setReportData({
        totalPlots: data.totalPlots || 0,
        totalPaid: data.totalPaid || 0,
        totalPurchasePrice: data.totalPurchasePrice || 0,
        projectStats: Array.isArray(data.projectStats) ? data.projectStats : [],
        statusDistribution: Array.isArray(data.statusDistribution)
          ? data.statusDistribution
          : [],
        recentActivity: Array.isArray(data.recentActivity)
          ? data.recentActivity
          : [],
      });
    } catch (err) {
      console.error("Error fetching report data:", err);
      setError("Failed to load report data. Please try again.");
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchReportData();
      setLoading(false);
    };
    load();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchReportData();
    setRefreshing(false);
  };

  const handlePrint = () => window.print();

  // Compute variance
  const variance = reportData.totalPaid - reportData.totalPurchasePrice;

  // Status colour helper
  const statusColor = (status) => {
    const s = status.toLowerCase();
    if (s.includes("approved") || s.includes("discharged"))
      return "bg-green-500";
    if (s.includes("initiated") || s.includes("submitted"))
      return "bg-blue-500";
    if (s.includes("rejected")) return "bg-red-500";
    if (s.includes("pending") || s.includes("level")) return "bg-yellow-500";
    return "bg-gray-400";
  };

  // Activity icon helper
  const activityIcon = (action) => {
    const a = (action || "").toLowerCase();
    if (a.includes("approved") || a.includes("discharged"))
      return { Icon: CheckCircle, bg: "bg-green-500" };
    if (a.includes("rejected"))
      return { Icon: AlertTriangle, bg: "bg-red-500" };
    if (a.includes("initiated") || a.includes("submitted"))
      return { Icon: Clock, bg: "bg-blue-500" };
    return { Icon: FileText, bg: "bg-gray-500" };
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-lg font-medium text-gray-700">
              Loading reports...
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Please wait while we fetch the latest data
            </p>
          </div>
        </div>
      </Sidebar>
    );
  }

  if (error) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              Error Loading Reports
            </h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Analytics & Reports
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Comprehensive insights into discharge reconciliation
                  performance
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${
                      refreshing ? "animate-spin" : ""
                    }`}
                  />
                  Refresh
                </button>
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-6 py-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Building className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Total Plots
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {reportData.totalPlots.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Total Paid
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    Ksh {reportData.totalPaid.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-teal-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Total Purchase Price
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    Ksh {reportData.totalPurchasePrice.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div
                    className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      variance >= 0 ? "bg-yellow-100" : "bg-red-100"
                    }`}
                  >
                    {variance >= 0 ? (
                      <TrendingUp className="h-6 w-6 text-yellow-600" />
                    ) : (
                      <TrendingDown className="h-6 w-6 text-red-600" />
                    )}
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Overall Variance
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      variance >= 0 ? "text-yellow-600" : "text-red-600"
                    }`}
                  >
                    {variance >= 0 ? "+" : ""}Ksh {variance.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Charts and Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Project Performance */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Project Performance
                </h3>
                <BarChart3 className="h-5 w-5 text-gray-400" />
              </div>
              <div className="space-y-4">
                {reportData.projectStats.length > 0 ? (
                  reportData.projectStats.map((project, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Building className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {project.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {project.plots} plots
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          Ksh {(project.totalPaid || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">Total Paid</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Building className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm text-gray-500">
                      No project data available
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Status Distribution */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Discharge Status Distribution
                </h3>
                <PieChart className="h-5 w-5 text-gray-400" />
              </div>
              <div className="space-y-3">
                {reportData.statusDistribution.length > 0 ? (
                  reportData.statusDistribution.map((status, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center flex-1">
                        <div
                          className={`h-3 w-3 rounded-full ${statusColor(
                            status.status,
                          )}`}
                        ></div>
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          {status.status}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-gray-900">
                          {status.count}
                        </span>
                        <span className="text-xs text-gray-500 w-14 text-right">
                          ({status.percentage}%)
                        </span>
                        {/* Progress bar */}
                        <div className="w-24 bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${statusColor(
                              status.status,
                            )}`}
                            style={{ width: `${status.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <PieChart className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm text-gray-500">
                      No status data available
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Recent Discharge Activity
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Latest plot discharge updates
              </p>
            </div>
            <div className="p-6">
              {reportData.recentActivity.length > 0 ? (
                <div className="flow-root">
                  <ul className="-mb-8">
                    {reportData.recentActivity.map((activity, activityIdx) => {
                      const { Icon, bg } = activityIcon(activity.action);
                      return (
                        <li key={activity.id}>
                          <div className="relative pb-8">
                            {activityIdx !==
                            reportData.recentActivity.length - 1 ? (
                              <span
                                className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                                aria-hidden="true"
                              />
                            ) : null}
                            <div className="relative flex space-x-3">
                              <div>
                                <span
                                  className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${bg}`}
                                >
                                  <Icon className="h-4 w-4 text-white" />
                                </span>
                              </div>
                              <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                <div>
                                  <p className="text-sm text-gray-500">
                                    <span className="font-medium text-gray-900">
                                      {activity.action}
                                    </span>{" "}
                                    for plot{" "}
                                    <span className="font-medium text-gray-900">
                                      {activity.plot}
                                    </span>{" "}
                                    — {activity.customer}
                                  </p>
                                </div>
                                <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                  {activity.timestamp}
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No recent activity
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No discharge activity has been recorded yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default ReportsPage;
